<script setup lang="ts">
import { identity, pickBy } from "lodash-es";
import { computed } from "vue";

import type {
  DictsysGermanAdjectiveInflectionScheme,
  DictsysInflectionSchemeGermanAdjectiveComparisonDegrees,
  DictsysInflectionSchemeGermanAdjectiveComparisons
} from "@/types/article-formats/dictsys";

import ComparisonScheme from "./adjectives/ComparisonScheme.vue";
import InflectionScheme from "./adjectives/InflectionScheme.vue";

interface Props {
  inflectionScheme: NonNullable<DictsysGermanAdjectiveInflectionScheme["adjective"]>;
  hideComparisons?: boolean;
}

const props = withDefaults(defineProps<Props>(), { hideComparisons: false });

const emits = defineEmits<{
  showTab: [name: string];
}>();

const makeComparisonsOnlyScheme = (
  inflectionScheme: NonNullable<DictsysGermanAdjectiveInflectionScheme["adjective"]>
) => {
  // Numeric index = comparison scheme from 0-7. "Gesund" is an example of a word with 8 comparisons.
  //
  // This means that for the comparisons it's as simple as:
  // inflectionScheme.[comparison].[0]     = always 1st degree
  // inflectionScheme.[comparison].[1]     = always 2nd degree
  // inflectionScheme.[comparison].[{2-7}] = third degree - first one is always "am", last ones are always der/die/das

  const mutableComparisonScheme: DictsysInflectionSchemeGermanAdjectiveComparisons = {
    positive: {
      "0": inflectionScheme.positive["0"] // 1st degree
    }
  };

  if (inflectionScheme.comparative) {
    mutableComparisonScheme.comparative = pickBy(
      {
        "0": inflectionScheme.comparative["0"], // 1st degree
        "1": inflectionScheme.comparative["1"] // 2nd degree
      },
      identity
    );
  }

  if (inflectionScheme.superlative) {
    mutableComparisonScheme.superlative = pickBy(
      {
        "0": inflectionScheme.superlative["0"], // 1st degree - am
        "1": inflectionScheme.superlative["1"], // 2nd degree - am
        "2": inflectionScheme.superlative["2"], // 3rd degree - der
        "3": inflectionScheme.superlative["3"], // 3rd degree - der
        "4": inflectionScheme.superlative["4"], // 3rd degree - die
        "5": inflectionScheme.superlative["5"], // 3rd degree - die
        "6": inflectionScheme.superlative["6"], // 3rd degree - das
        "7": inflectionScheme.superlative["7"] // 3rd degree - das
      },
      identity
    );
  }

  return mutableComparisonScheme;
};

const makeInflectionsOnlyScheme = (
  inflectionScheme: NonNullable<DictsysGermanAdjectiveInflectionScheme["adjective"]>
) => {
  // Weak = Bestemt = inflections scheme
  // Mixed = Ubestemt = inflections scheme
  // Strong = Uden kendeord = inflections scheme
  //
  // Structure for top inflection schemes can be translated to inflectionScheme.[comparison].[tab].[sense].[gender]
  // sense = left label, gender = top label, except for plural - they don't contain genders.
  // Basically, once you hit a numeric key in the structure, it's something you can render out

  // We only use "positive" as inflections as the rest are out of scope for school students.
  const mutableInflectionScheme: DictsysInflectionSchemeGermanAdjectiveComparisonDegrees = {
    positive: {
      weak: inflectionScheme.positive.weak,
      mixed: inflectionScheme.positive.mixed,
      strong: inflectionScheme.positive.strong
    }
  };

  return mutableInflectionScheme;
};

const schemes = computed(() => {
  const comparisonScheme = makeComparisonsOnlyScheme(props.inflectionScheme);
  const inflectionScheme = makeInflectionsOnlyScheme(props.inflectionScheme);

  return {
    // Bøjningsskema
    inflectionScheme,

    // Gradbøjninger
    comparisonScheme
  };
});
</script>

<template>
  <div>
    <InflectionScheme
      :inflection-scheme="schemes.inflectionScheme"
      @show-tab="emits('showTab', $event)"
    >
      <template #inflection-cell="context">
        <slot name="inflection-cell" v-bind="context" />
      </template>
    </InflectionScheme>
    <template v-if="!hideComparisons">
      <div class="inflection-scheme-modal__separator"></div>
      <ComparisonScheme :comparison-scheme="schemes.comparisonScheme" />
    </template>
  </div>
</template>
